// Codex. Progressive cart drawer: server-rendered content, native checkout, optional protection.
import {CartClient} from './bs-cart-client.js?v=20261016a';
import {BONUS_CODE,BONUS_KEY,bonusState,bonusDiscounts} from './bs-bonus-model.js?v=0f30b54aebc96d8e';
import {markExtraItems} from './bs-extras-model.js?v=0f30b54aebc96d8e';
import {CHOICE_KEY, protectionState, nextProtectionOperation, validQuantity, pageMarkup} from './bs-cart-model.js?v=20261016a';
const one=(s,root=document)=>root.querySelector(s), all=(s,root=document)=>[...root.querySelectorAll(s)];
const root=window.Shopify?.routes?.root || '/';
let config={}, opener=null, pending=0, previousOverflow='', restoreFocus=null;
function readConfig(){const el=one('[data-bsc-config]');if(el)config=JSON.parse(el.textContent);return config;}
readConfig();
const client=new CartClient({root,config:()=>config});
const drawer=()=>one('#BSCartDrawer');
function announce(message=''){all('[data-bsc-status]').forEach(el=>el.textContent=message);}
function showError(message='') {all('[data-bsc-error]').forEach(el=>{el.hidden=!message;el.textContent=message;});}
function setBusy(busy) {
  all('.bsc').forEach(el=>el.setAttribute('aria-busy',String(busy)));
  all('[data-bsc-checkout], [data-bsc-step], [data-bsc-quantity], [data-bsc-add], [data-bsc-protection], [data-bsc-variant]').forEach(el=>el.disabled=busy);
  all('[data-bsc-remove]').forEach(el=>el.setAttribute('aria-disabled',String(busy)));
}
function openDrawer(trigger=document.activeElement) {
  if(!config.drawer || !drawer()?.showModal)return false;
  if(!drawer().open){opener=trigger;previousOverflow=document.documentElement.style.overflow;document.documentElement.style.overflow='hidden';drawer().showModal();one('[data-bsc-close]',drawer())?.focus();}
  all('.bs-mobile-menu[open]').forEach(menu=>menu.removeAttribute('open')); return true;
}
function closeDrawer(){drawer()?.close();}
function count(cart) {
  const number=protectionState(cart,config).merchandise.reduce((sum,item)=>sum+item.quantity,0);
  all('[data-bsc-count]').forEach(el=>el.textContent=String(number));
}
// Claude 2026-09-23: renderCart used location.href + Accept: application/json. On a product URL Shopify answers
// that with the PRODUCT's JSON, so the drawer came back empty and every refresh fell back to downloading the
// whole /cart page (~0.5 s). The drawer section is now requested from the store root, and add.js returns it
// directly (bundled section rendering) so a new item paints after one round trip.
function drawerUrl(){
  const url=new URL(root,location.origin);url.searchParams.set('sections','bs-cart-drawer');
  if(config.previewTheme)url.searchParams.set('preview_theme_id',String(config.previewTheme));
  return url;
}
async function fetchDrawerHtml(){
  const response=await fetch(drawerUrl(),{credentials:'same-origin',cache:'no-store',signal:AbortSignal.timeout(20000)});
  if(!response.ok)throw new Error('Your order is saved, but the cart display could not refresh. Open the full cart to review it.');
  let html=null;try{html=(await response.json())['bs-cart-drawer'];}catch{}
  if(!html){
    // Shopify can omit a static section in a preview context. The full cart is a native fallback.
    const fallback=new URL(root+'cart',location.origin);if(config.previewTheme)fallback.searchParams.set('preview_theme_id',String(config.previewTheme));
    const full=await fetch(fallback,{credentials:'same-origin',cache:'no-store',signal:AbortSignal.timeout(20000)});
    if(full.ok)html=await full.text();
  }
  if(!html)throw new Error('The cart display could not refresh. Open the full cart to review your order.');
  return html;
}
function paint(html){
  const doc=new DOMParser().parseFromString(html,'text/html'),fresh=one('#BSCartDrawer [data-bsc-content]',doc);
  if(!fresh)throw new Error('The cart display could not refresh. Open the full cart to review your order.');
  const old=one('[data-bsc-content]',drawer()), scroll=one('.bsc-scroll',old)?.scrollTop || 0;
  const active=document.activeElement, focusId=active?.id;
  if(old)old.replaceWith(fresh.cloneNode(true));
  const page=one('[data-bsc-page]'); if(page)page.innerHTML=pageMarkup(fresh.outerHTML);
  const newConfig=one('[data-bsc-config]',doc);if(newConfig)config=JSON.parse(newConfig.textContent);
  const scrollEl=one('.bsc-scroll',drawer());if(scrollEl)scrollEl.scrollTop=scroll;
  setBusy(pending>0);
  if(active && !active.isConnected){
    const label=active.getAttribute('aria-label'),scope=drawer()?.open?drawer():page;
    restoreFocus=()=>{
      const target=(focusId&&document.getElementById(focusId)) || (label&&all('[aria-label]',scope).find(el=>el.getAttribute('aria-label')===label)) || one('[data-bsc-checkout], [data-bsc-close]',scope);
      target?.focus({preventScroll:true});
    };
  }
}
function cartUpdated(cart){
  count(cart);
  document.dispatchEvent(new CustomEvent('bookscription:cart-updated',{detail:{itemCount:cart.item_count,total:cart.total_price,currency:cart.currency}}));
}
async function renderCart(cart, html=null) {
  paint(html || await fetchDrawerHtml());
  cartUpdated(cart);
}
// Claude 2026-09-23: remember the last verified cart so an add can include shipping protection in the SAME
// add.js request (saves a second ~0.7 s add + re-render). reconcile() still verifies and corrects afterwards.
let lastCart=null;
(window.requestIdleCallback||(f=>setTimeout(f,300)))(()=>client.read().then(c=>{lastCart=lastCart||c;}).catch(()=>{}));
function predictedProtection(items){
  if(!lastCart)return [];
  try{
    const extraIds=new Set((config.extras||[]).flatMap(e=>e.variants||[]).map(Number)),bonusId=Number(config.bonus?.variant);
    const added=items.map(i=>({product_id:extraIds.has(Number(i.id))||Number(i.id)===bonusId?-1:Number(config.boxProduct),
      variant_id:Number(i.id),quantity:Number(i.quantity)||1,key:'predicted',properties:i.properties||{},
      selling_plan_allocation:i.selling_plan?{selling_plan:{id:Number(i.selling_plan)}}:null}));
    const op=nextProtectionOperation({...lastCart,items:[...lastCart.items,...added]},config);
    return op&&!op.error&&op.route==='add.js'?op.body.items:[];
  }catch{return [];}
}
function startAdding(){
  const d=drawer();if(!d)return;
  if(!one('.bsc-skeleton',d)){const s=document.createElement('div');s.className='bsc-skeleton';s.setAttribute('aria-hidden','true');s.innerHTML='<p>Packing your box<span>…</span></p><i></i><i></i><i></i>';one('[data-bsc-content]',d)?.before(s);}
  d.classList.add('bsc-adding');announce('Adding to your book bag…');
}
function stopAdding(){drawer()?.classList.remove('bsc-adding');}
async function refresh(){const cart=await client.reconcile();lastCart=cart;await renderCart(cart);return cart;}
function run(task) {
  pending++; setBusy(true);
  return client.serial(async()=>{
    const work=async()=>{showError();announce('Updating your book bag…');try{return await task();}finally{announce();}};
    return navigator.locks?.request ? navigator.locks.request('bookscription-cart',work) : work();
  }).finally(()=>{pending--;setBusy(pending>0);if(!pending&&restoreFocus){const focus=restoreFocus;restoreFocus=null;if(document.activeElement===document.body||document.activeElement===drawer())focus();}});
}
async function recover(error) {
  try {await renderCart(await client.read());}catch{/* Keep existing server-rendered full-cart link available. */}
  showError(error.message || 'Your cart could not be updated. Please try again.');
}
export async function addItems(items) {
  items=markExtraItems(items,config);
  const opened=openDrawer();
  if(opened)startAdding();
  return run(async()=>{
    let added;
    try {added=await client.request('add.js',{items:[...items,...predictedProtection(items)],sections:'bs-cart-drawer',sections_url:root});}
    catch(error){stopAdding();await recover(error);if(!opened)throw error;return {added:false,uncertain:Boolean(error.uncertain)};}
    // Paint the drawer Shopify rendered with the add, then verify protection/bonus/extras in the background.
    // Checkout stays disabled (run() keeps the cart busy) until the verification finishes.
    const early=added?.sections?.['bs-cart-drawer'];
    if(early&&opened){try{paint(early);}catch{}}
    stopAdding();
    // A failed protection/display refresh must never invite a second merchandise add.
    try {const cart=await client.reconcile();lastCart=cart;if(client.changed||!early)await renderCart(cart);else cartUpdated(cart);announce('Added to your book bag.');}
    catch(error){await recover(error);}
    if(!opened)location.assign(root+'cart');
    return {added:true};
  });
}
async function changeQuantity(line, value) {
  let quantity;try{quantity=validQuantity(value);}catch(error){showError(error.message);return;}
  await run(async()=>{try{await client.request('change.js',{id:line.dataset.bscLine,quantity});await refresh();}catch(error){await recover(error);}});
}
document.addEventListener('click',async event=>{
  const el=event.target.closest('button,a');if(!el)return;
  if(el.matches('[data-bsc-close]')){closeDrawer();return;}
  if(el.matches('[data-bsc-refresh]')){event.preventDefault();await run(async()=>{try{await refresh();}catch(error){await recover(error);}});return;}
  if(el.matches('a[href]') && !el.matches('[data-bsc-full-cart]') && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && event.button===0){
    const url=new URL(el.href,location.href);
    if(url.origin===location.origin && url.pathname.replace(/\/$/,'')===(root+'cart').replace(/\/$/,'')){
      if(openDrawer(el)){event.preventDefault();await run(async()=>{try{await refresh();}catch(error){await recover(error);}});}return;
    }
  }
  if(pending && el.closest('.bsc') && (el.matches('[data-bsc-remove],[data-bsc-step],[data-bsc-add]'))){event.preventDefault();return;}
  if(el.matches('[data-bsc-remove]')){event.preventDefault();const line=el.closest('[data-bsc-line]');if(line.dataset.bscBonus){await run(async()=>{try{await client.request('update.js',{attributes:{[BONUS_KEY]:'off'}});await refresh();}catch(error){await recover(error);}});}else await changeQuantity(line,0);}
  if(el.matches('[data-bsc-bonus-cancel]')){event.preventDefault();await run(async()=>{try{await client.request('update.js',{attributes:{[BONUS_KEY]:'off'}});await refresh();}catch(error){await recover(error);}});} 
  if(el.matches('[data-bsc-step]')){const line=el.closest('[data-bsc-line]');await changeQuantity(line,Number(one('[data-bsc-quantity]',line).value)+Number(el.dataset.bscStep));}
  if(el.matches('[data-bsc-add]')){const offer=el.closest('[data-bsc-offer]');await addItems([{id:Number(one('[data-bsc-variant]',offer).value),quantity:1}]);}
});
document.addEventListener('change',async event=>{
  const el=event.target;
  if(el.matches('[data-bsc-quantity]'))await changeQuantity(el.closest('[data-bsc-line]'),el.value);
  if(el.matches('[data-bsc-protection]')){
    const choice=el.checked?'on':'off';await run(async()=>{try{await client.request('update.js',{attributes:{[CHOICE_KEY]:choice}});await refresh();}catch(error){await recover(error);}});
  }
  if(el.matches('select[data-bsc-variant]')){
    const option=el.selectedOptions[0],offer=el.closest('[data-bsc-offer]'),price=Number(option.dataset.price);
    const money=n=>new Intl.NumberFormat(document.documentElement.lang,{style:'currency',currency:offer.dataset.currency || 'USD'}).format(n/100);
    one('[data-bsc-offer-price]',offer).textContent=money(Math.ceil(price/2));one('[data-bsc-offer-base]',offer).textContent=money(price);
    const img=one('img',offer);if(img&&option.dataset.image){img.removeAttribute('srcset');img.src=option.dataset.image;}
  }
});
document.addEventListener('submit',async event=>{
  if(!event.target.matches('[data-bsc-form]'))return;event.preventDefault();if(pending)return;
  const checkout=event.submitter?.name==='checkout';
  await run(async()=>{try{
    await refresh();
    if(checkout){
      // Submit the native Shopify cart form, including checkout's successful-control value.
      const cartForm=one('[data-bsc-form]',drawer()?.open?drawer():one('[data-bsc-page]'));
      if(!cartForm)return;
      // Quantity changes have already been committed. Do not serialize disabled updates[].
      const form=document.createElement('form');form.action=root+'cart';form.method='post';form.hidden=true;document.body.append(form);
      const input=document.createElement('input');input.type='hidden';input.name='checkout';input.value='Checkout';form.append(input);
      HTMLFormElement.prototype.submit.call(form);
    }
  }catch(error){await recover(error);}});
});
drawer()?.addEventListener('close',()=>{
  document.documentElement.style.overflow=previousOverflow;
  const visible=element=>element?.isConnected&&element.getClientRects().length&&!element.disabled;
  const target=visible(opener)?opener:all('header a[href], .bs-mobile-menu>summary').find(visible)||one('main');
  if(target){if(!target.matches('a,button,summary,input,select'))target.tabIndex=-1;target.focus({preventScroll:true});}
});
drawer()?.addEventListener('click',event=>{if(event.target===drawer()){const box=drawer().getBoundingClientRect();if(event.clientX<box.left || event.clientX>box.right || event.clientY<box.top || event.clientY>box.bottom)closeDrawer();}});
if(one('[data-bsc-page]'))run(async()=>{try{await refresh();}catch(error){await recover(error);}});
window.addEventListener('pageshow',event=>{if(event.persisted && (one('[data-bsc-page]')||drawer()?.open))run(async()=>{try{await refresh();}catch(error){await recover(error);}});});

// Reserve on arrival; only report added/free after Shopify confirms a qualifying cart.
const bonusDialog=one('#BSBonusOffer');
let bonusOpener=null,bonusOverflow='';
function closeBonus(){bonusDialog?.close();}
all('[data-bs-bonus-close]').forEach(el=>el.addEventListener('click',closeBonus));
bonusDialog?.addEventListener('close',()=>{document.documentElement.style.overflow=bonusOverflow;const target=bonusOpener?.isConnected?bonusOpener:one('[data-bp-plan][value="monthly"]');target?.focus({preventScroll:true});});
async function reserveBonus(){
  if(!bonusDialog||!config.bonus?.enabled)return;
  bonusOpener=document.activeElement;bonusOverflow=document.documentElement.style.overflow;
  document.documentElement.style.overflow='hidden';bonusDialog.showModal();
  const message=one('[data-bs-bonus-message]',bonusDialog),progress=one('progress',bonusDialog);
  try{
    await run(async()=>{
      const current=await client.read();progress.value=1;
      await client.request('update.js',{attributes:{[BONUS_KEY]:BONUS_CODE},discount:bonusDiscounts(current)});
      const cart=await client.reconcile();progress.value=2;
      const state=bonusState(cart,config);
      message.textContent=state.gifts.some(g=>g.final_line_price===0)?'Your two bonus books are in your bag, free with your first monthly box.':'Your bonus is saved. Add your first monthly box and we will add two extra books free.';
      if(drawer()?.open||one('[data-bsc-page]'))await renderCart(cart);
    });
    const clean=new URL(location.href);clean.searchParams.delete('offer');history.replaceState(history.state,'',clean);
  }catch(error){message.textContent=error.message;one('[data-bs-bonus-help]',bonusDialog).hidden=false;}
}
const bonusQuery=new URLSearchParams(location.search);
if((bonusQuery.get('offer')||'').toUpperCase()===BONUS_CODE)reserveBonus();
// Claude 2026-09-23: single shared instance for bs-commerce.js.
window.__bsCartModule=window.__bsCartModule||{addItems};
